import { KeyboardResize } from '@capacitor/keyboard';
import { isPlatform } from '@ionic/core';
import { IonContent } from '@ionic/react';
import { animated, useSpring } from '@react-spring/web';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import {
  IOS_KEYBOARD_RESIZE_MODE,
  useKeyboardWillHide,
  useKeyboardWillShow,
} from '../../../theme/keyboard.hook';
import { PanelBaseProps } from './content-panel';
import './panels.css';

export interface SidePanelProps extends PanelBaseProps {
  enabledBottomBreak?: boolean;
}

const resizeSidePanelForKeyboard = (keyboardHeight?: number) => {
  // do not apply resize for android. the android os takes care
  // of resizing the panel on its own
  if (isPlatform('android')) {
    return;
  }

  const sel = `ion-popover.side-panel-menu div.popover-content`;
  const el = document.querySelector<HTMLDivElement>(sel);

  if (el) {
    let clientHeight = window.innerHeight;

    switch (IOS_KEYBOARD_RESIZE_MODE) {
      case KeyboardResize.Body:
        clientHeight = document.body.clientHeight;
        break;
      default:
        break;
    }

    const heightStr = keyboardHeight
      ? `${clientHeight - keyboardHeight}px`
      : '100%';

    el.style.setProperty('height', heightStr);

    // the min-height prevents the popover from resizing even
    // if the window height or body height changes
    el.style.setProperty('--min-height', heightStr);
  }
};

const SidePanel = (props: SidePanelProps): ReactElement | null => {
  const { children, show: externalShow, onDidDismiss } = props;

  const [show, setShow] = useState<boolean>(externalShow ?? false);
  const [isVisible, setVisible] = useState<boolean>(show ?? false);
  const pendingCallbackRef = useRef<(() => void) | undefined>();

  useEffect(() => {
    setShow(externalShow ?? false);
  }, [externalShow]);

  useKeyboardWillShow((e) => {
    resizeSidePanelForKeyboard(e.keyboardHeight);
  });

  useKeyboardWillHide(() => {
    resizeSidePanelForKeyboard();
  });

  const dismiss = useCallback(async () => {
    setShow(false);
  }, []);

  const didDismiss = useCallback(async () => {
    if (onDidDismiss) {
      await onDidDismiss();
    }
    if (pendingCallbackRef.current) {
      pendingCallbackRef.current();
      pendingCallbackRef.current = undefined;
    }
  }, [onDidDismiss]);

  const onClose = useCallback(
    async (cb: () => void) => {
      pendingCallbackRef.current = cb;
      dismiss();
    },
    [dismiss]
  );

  const { transform } = useSpring({
    transform: show ? `translateX(0px)` : `translateX(500px)`,
    springStatus: show ? 1 : 0,
    config: {
      // it seems we need a duration here to be sure the side panel animates closed before
      // calling the didDismiss.  without this, we continue to have the issue where the entire
      // ion-content is shifted off to the right
      duration: 300,
    },
    onRest: ({ value: { springStatus } }) => {
      // the IF condition below only triggers when the panel is completely closed (come to rest)
      if (springStatus === 0) {
        if (isVisible === true) {
          setVisible(false);
          didDismiss();
        }
      }
    },
    onStart: () => {
      if (show === true) {
        setVisible(true);
      }
    },
  });

  return (
    <animated.div
      // this component is a direct child of ion-content
      // slot=fixed applies the proper fixed styling we need
      slot="fixed"
      className="side-panel-menu"
      style={{
        transform,
      }}
    >
      {isVisible && (
        <IonContent>
          {typeof children === 'function'
            ? children({ dismiss, onClose })
            : children}
        </IonContent>
      )}
    </animated.div>
  );
};

export { SidePanel };
